import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, money, pct } from "@/lib/api";
import Testimonials from "@/components/site/Testimonials";

export default function Markets() {
  const [stocks, setStocks] = useState([]);
  const [q, setQ] = useState("");
  const [sector, setSector] = useState("All");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/stocks").then(({ data }) => setStocks(data)).catch(() => {});
  }, []);

  const sectors = useMemo(() => ["All", ...new Set(stocks.map((s) => s.sector).filter(Boolean))], [stocks]);
  const rows = stocks.filter(
    (s) => (sector === "All" || s.sector === sector) && `${s.ticker} ${s.name}`.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <>
    <div className="container-x py-16" data-testid="markets-page">
      <p className="eyebrow">Markets</p>
      <h1 className="mt-3 font-display text-4xl font-semibold tracking-tighter text-white sm:text-5xl">Stocks</h1>
      <p className="mt-4 max-w-xl text-slate-400">Prices are published by Meridian and updated by our team.</p>

      <div className="mt-10 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="relative w-full md:max-w-sm">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ticker or company" className="field pl-11" data-testid="markets-search-input" />
        </div>
        <div className="chip-row">
          {sectors.map((s) => (
            <button key={s} onClick={() => setSector(s)} data-testid={`markets-sector-${s.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-full border px-4 py-3 text-xs transition-colors duration-200 sm:py-1.5 ${sector === s ? "border-emerald-500 bg-emerald-500/10 text-emerald-400" : "border-line text-slate-400 hover:text-white"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 space-y-2 md:hidden" data-testid="markets-list-mobile">
        {rows.map((s) => (
          <Link key={s.id} to={`/markets/${s.id}`} className="panel flex items-center justify-between gap-4 px-4 py-4 transition-colors duration-150 active:bg-white/5" data-testid={`stock-card-mobile-${s.ticker}`}>
            <div className="min-w-0">
              <p className="font-mono font-semibold tracking-tight text-white">{s.ticker}</p>
              <p className="truncate text-sm text-slate-400">{s.name}</p>
            </div>
            <div className="shrink-0 text-right">
              <p className="font-mono tracking-tight text-white">{money(s.price)}</p>
              <p className={`font-mono text-xs ${s.change_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>{pct(s.change_pct)}</p>
            </div>
          </Link>
        ))}
        {!rows.length && <p className="py-12 text-center text-slate-500" data-testid="markets-empty-mobile">No stocks match your search.</p>}
      </div>

      <div className="panel mt-8 hidden overflow-x-auto md:block">
        <table className="w-full min-w-[640px] text-left text-sm" data-testid="markets-table">
          <thead>
            <tr className="border-b border-line text-slate-500">
              <th className="px-6 py-4 font-medium">Ticker</th>
              <th className="px-6 py-4 font-medium">Company</th>
              <th className="px-6 py-4 font-medium">Sector</th>
              <th className="px-6 py-4 text-right font-medium">Price</th>
              <th className="px-6 py-4 text-right font-medium">Day change</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} onClick={() => navigate(`/markets/${s.id}`)} className="cursor-pointer border-b border-line/60 transition-colors duration-150 last:border-0 hover:bg-white/[0.03]" data-testid={`stock-row-${s.ticker}`}>
                <td className="px-6 py-5 font-mono font-semibold tracking-tight text-white">{s.ticker}</td>
                <td className="px-6 py-5 text-slate-300">{s.name}</td>
                <td className="px-6 py-5 text-slate-400">{s.sector}</td>
                <td className="px-6 py-5 text-right font-mono tracking-tight text-white">{money(s.price)}</td>
                <td className={`px-6 py-5 text-right font-mono ${s.change_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>{pct(s.change_pct)}</td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={5} className="px-6 py-12 text-center text-slate-500" data-testid="markets-empty">No stocks match your search.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
    <Testimonials category="stocks" />
    </>
  );
}
